import type { FieldDefinition, IntegrationProviderConfig } from '../../types';
import StripeIcon from './StripeIcon';

const pullObjectTypes = [
  { label: 'Customers', value: 'customers' },
  { label: 'Charges', value: 'charges' },
  { label: 'Invoices', value: 'invoices' },
  { label: 'Subscriptions', value: 'subscriptions' },
  { label: 'Payment intents', value: 'payment_intents' },
  { label: 'Payouts', value: 'payouts' },
  { label: 'Products', value: 'products' },
  { label: 'Prices', value: 'prices' },
  { label: 'Balance', value: 'balance' },
  { label: 'Disputes', value: 'disputes' },
];

const pushActions = [
  { label: 'Create invoice', value: 'create_invoice' },
  { label: 'Create customer', value: 'create_customer' },
  { label: 'Update customer', value: 'update_customer' },
  { label: 'Create product', value: 'create_product' },
  { label: 'Create price', value: 'create_price' },
  { label: 'Finalize invoice', value: 'finalize_invoice' },
  { label: 'Send invoice', value: 'send_invoice' },
];

const pullSharedFilters = (objectType: string): FieldDefinition[] => {
  if (objectType === 'balance') {
    return [];
  }

  const dateRow: FieldDefinition[] = [
    {
      name: 'dateFrom',
      label: 'Date from',
      type: 'date',
    },
    {
      name: 'dateTo',
      label: 'Date to',
      type: 'date',
    },
  ];

  const statusEligible = [
    'charges',
    'invoices',
    'subscriptions',
    'payment_intents',
    'payouts',
    'disputes',
    'products',
    'prices',
  ].includes(objectType);

  const statusField: FieldDefinition[] = statusEligible
    ? [
        {
          name: 'statusFilter',
          label: 'Status',
          type: 'select',
          options: 'dynamic',
          placeholder: 'Any',
        },
      ]
    : [];

  const productIdField: FieldDefinition[] =
    objectType === 'prices'
      ? [
          {
            name: 'productId',
            label: 'Product ID',
            type: 'select',
            options: 'dynamic',
            remoteSearch: true,
            placeholder: 'Search products (optional)',
          },
        ]
      : [];

  return [
    ...dateRow,
    ...statusField,
    ...productIdField,
    {
      name: 'customerId',
      label: 'Customer ID',
      type: 'select',
      options: 'dynamic',
      remoteSearch: true,
      placeholder: 'Search customers (optional)',
      hasConnectionPoint: true,
    },
    {
      name: 'limit',
      label: 'Limit',
      type: 'number',
      defaultValue: 20,
      placeholder: 'Max 100',
    },
    {
      name: 'searchQuery',
      label: 'Search query',
      type: 'textarea',
      placeholder: 'Stripe Search query (when supported for this object)',
      hasConnectionPoint: true,
    },
  ];
};

function getPullFields(objectType: string): FieldDefinition[] {
  return pullSharedFilters(objectType);
}

function getPushFields(action: string): FieldDefinition[] {
  const metadataField: FieldDefinition = {
    name: 'metadataJson',
    label: 'Metadata (JSON object)',
    type: 'textarea',
    placeholder: '{"order_id":"123"}',
  };

  if (action === 'create_customer') {
    return [
      {
        name: 'customerEmail',
        label: 'Email',
        type: 'text',
        validation: { required: true },
      },
      {
        name: 'customerName',
        label: 'Name',
        type: 'text',
      },
      metadataField,
    ];
  }

  if (action === 'update_customer') {
    return [
      {
        name: 'customerId',
        label: 'Customer ID',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        validation: { required: true },
        placeholder: 'Search customers',
      },
      {
        name: 'customerEmail',
        label: 'Email',
        type: 'text',
      },
      {
        name: 'customerName',
        label: 'Name',
        type: 'text',
      },
      metadataField,
    ];
  }

  if (action === 'create_product') {
    return [
      {
        name: 'productName',
        label: 'Product name',
        type: 'text',
        validation: { required: true },
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
      },
      metadataField,
    ];
  }

  if (action === 'create_price') {
    return [
      {
        name: 'productId',
        label: 'Product ID',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        validation: { required: true },
        placeholder: 'Search products',
      },
      {
        name: 'amountDollars',
        label: 'Unit amount (dollars)',
        type: 'number',
        validation: { required: true },
      },
      {
        name: 'currency',
        label: 'Currency',
        type: 'text',
        defaultValue: 'usd',
        validation: { required: true },
      },
      {
        name: 'priceRecurringInterval',
        label: 'Recurring interval',
        type: 'select',
        options: [
          { label: 'One-time', value: '' },
          { label: 'Monthly', value: 'month' },
          { label: 'Yearly', value: 'year' },
        ],
      },
    ];
  }

  if (action === 'create_invoice') {
    return [
      {
        name: 'customerId',
        label: 'Customer ID',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        validation: { required: true },
        placeholder: 'Search customers',
      },
      {
        name: 'currency',
        label: 'Currency',
        type: 'text',
        defaultValue: 'usd',
        validation: { required: true },
      },
      {
        name: 'amountDollars',
        label: 'Amount (dollars)',
        type: 'number',
        placeholder: 'Optional line item amount',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
      },
      {
        name: 'dueDate',
        label: 'Due date',
        type: 'datetime',
      },
      metadataField,
    ];
  }

  if (action === 'finalize_invoice' || action === 'send_invoice') {
    return [
      {
        name: 'invoiceId',
        label: 'Invoice ID',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        validation: { required: true },
        placeholder: 'Search invoices',
      },
    ];
  }

  return [];
}

export const stripeConfig: IntegrationProviderConfig = {
  id: 'stripe',
  name: 'Stripe',
  icon: StripeIcon,
  brandColor: '#635BFF',
  authMethod: 'oauth2',
  authFields: [
    {
      name: 'stripeSecretPassword',
      label: 'Secret key',
      type: 'text',
      placeholder: 'sk_test_... or sk_live_...',
      validation: { required: true },
    },
    {
      name: 'stripeMode',
      label: 'Mode',
      type: 'select',
      defaultValue: 'test',
      options: [
        { label: 'Test', value: 'test' },
        { label: 'Live', value: 'live' },
      ],
      validation: { required: true },
    },
  ],
  pullConfig: {
    objectTypes: pullObjectTypes,
    getFields: getPullFields,
  },
  pushConfig: {
    actions: pushActions,
    getFields: getPushFields,
  },
  supportsPull: true,
  supportsPush: true,
};
